import {
  Component, Input, Output, EventEmitter,
  ChangeDetectionStrategy, OnChanges, SimpleChanges, OnDestroy,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FFlowModule, FCanvasComponent } from '@foblex/flow';
import { GraphNode, GraphEdge } from '../../../../core/models/chat.models';
import { NodeDetailsSidebarComponent } from '../node-details-sidebar/node-details-sidebar.component';
import { EdgeDetailsSidebarComponent } from '../edge-details-sidebar/edge-details-sidebar.component';

// ─── Layout constants ────────────────────────────────────────────────────────
const NODE_W = 160;   // node width  (px)
const H_GAP = 80;    // horizontal gap between nodes in a row
const ROW_H = 180;   // vertical distance between row tops
const MAX_PER_ROW = 100;     // wrap after this many nodes
const CANVAS_CENTER = 600;   // positive X centre so all nodes are visible

// Layer order: smaller = higher on canvas
const LAYER: Record<string, number> = {
  '__entity__': 0,
  '__fact__': 2,
  '__statement__': 3,
  '__topic__': 4,
  '__chunk__': 5,
  '__source__': 6,
};

function layerOf(label: string): number {
  return LAYER[(label || '').toLowerCase()] ?? 7;
}

export interface PositionedNode extends GraphNode {
  pos: { x: number; y: number };
  displayColor: string;
  displayStroke: string;
  displayType: string;
  displayContent: string;
}

@Component({
  selector: 'app-graph-viewer',
  imports: [
    CommonModule, FFlowModule, NodeDetailsSidebarComponent, EdgeDetailsSidebarComponent
  ],
  templateUrl: './graph-viewer.component.html',
  styleUrls: ['./graph-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GraphViewerComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() isLoading = false;
  @Input() graphNodes: GraphNode[] = [];
  @Input() graphEdges: GraphEdge[] = [];

  @Input() selectedNode: GraphNode | null = null;
  @Input() selectedEdge: GraphEdge | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() nodeClick = new EventEmitter<GraphNode>();
  @Output() detailsClose = new EventEmitter<void>();
  @Output() suggestChunkEdits = new EventEmitter<{ nodeId: string, oldValue: string, label: string, desiredChange: string }>();
  @Output() editChunksDirectly = new EventEmitter<{ nodeId: string, originalValue: string, editedValue: string }>();
  @Output() edgeClick = new EventEmitter<GraphEdge>();
  @Output() edgeDetailsClose = new EventEmitter<void>();

  @ViewChild(FCanvasComponent) fCanvasComponent?: FCanvasComponent;

  positionedNodes: PositionedNode[] = [];
  private isActuallyDragging = false;
  fitTimeout: any;

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['graphNodes'] && this.graphNodes) || (changes['isOpen'] && this.isOpen)) {
      if (this.graphNodes) {
        this.positionedNodes = this.computePositions(this.graphNodes);
      }

      // Auto-fit to screen when data changes or modal opens
      if (this.isOpen && !this.isLoading) {
        if (this.fitTimeout) clearTimeout(this.fitTimeout);
        this.fitTimeout = setTimeout(() => {
          this.fCanvasComponent?.fitToScreen({ x: 40, y: 40 }); // 40px padding
        }, 150);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.fitTimeout) {
      clearTimeout(this.fitTimeout);
    }
  }

  private computePositions(nodes: GraphNode[]): PositionedNode[] {
    // Group by layer
    const layerMap = new Map<number, GraphNode[]>();
    for (const n of nodes) {
      const l = layerOf(n.label);
      if (!layerMap.has(l)) layerMap.set(l, []);
      layerMap.get(l)!.push(n);
    }

    const result: PositionedNode[] = [];
    let rowTopY = 0;
    const sortedLayers = [...layerMap.entries()].sort(([a], [b]) => a - b);

    for (const [, layerNodes] of sortedLayers) {
      // Chunk into sub-rows if there are many nodes
      for (let i = 0; i < layerNodes.length; i += MAX_PER_ROW) {
        const row = layerNodes.slice(i, i + MAX_PER_ROW);
        const rowWidth = row.length * NODE_W + (row.length - 1) * H_GAP;
        let x = CANVAS_CENTER - rowWidth / 2;  // centre each row around a positive X

        for (const node of row) {
          result.push({
            ...node,
            pos: { x, y: rowTopY },
            displayColor: this.nodeColor(node.label),
            displayStroke: this.nodeStroke(node.label),
            displayType: (node.label || '').replace(/__/g, ''),
            displayContent: this.nodeContent(node),
          });
          x += NODE_W + H_GAP;
        }
        rowTopY += ROW_H;
      }
    }
    return result;
  }

  onNodeClick(node: PositionedNode): void {
    if (this.isActuallyDragging) {
      return;
    }
    this.nodeClick.emit(node);
  }

  onDragStarted(): void {
    this.isActuallyDragging = true;
  }

  onDragEnded(): void {
    // Use a small timeout to ensure the click event (which fires after mouseup) 
    // is blocked before we reset the flag.
    setTimeout(() => {
      this.isActuallyDragging = false;
    }, 150);
  }

  onEdgeClick(edge: GraphEdge): void {
    this.edgeClick.emit(edge);
  }

  // ─── Styling helpers ─────────────────────────────────────────────────────

  private nodeColor(label: string): string {
    const l = (label || '').toLowerCase();
    if (l === '__entity__') return '#FFFBEB';
    if (['__source__', '__chunk__'].includes(l)) return '#EFF6FF';
    return '#ECFDF5';
  }

  private nodeStroke(label: string): string {
    const l = (label || '').toLowerCase();
    if (l === '__entity__') return '#FBBF24';
    if (['__source__', '__chunk__'].includes(l)) return '#60A5FA';
    return '#34D399';
  }

  private nodeContent(node: GraphNode): string {
    const label = (node.label || '').replace(/__/g, '');
    const props = (node as any).properties || {};
    let content = '';
    if (label === 'Source') {
      const path = props.filePath || props.s3Key || 'No path';
      content = path.length > 60 ? '...' + path.slice(-57) : path;
    } else {
      const val = (props.value || '').toString();
      const words = val.split(/\s+/);
      content = words.length > 20 ? words.slice(0, 20).join(' ') + '...' : val;
    }
    if (content.length > 100) content = content.substring(0, 100) + '...';
    return content;
  }
}
